import { MutationCache, QueryClient, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiError } from './api';

/**
 * Controlled caching: no aggressive refetch, no polling by default (Supabase egress lessons
 * from GiveUp/Rise Up). Screens that need fresher data opt in explicitly and modestly.
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      },
      mutations: { retry: false },
    },
    mutationCache: new MutationCache(),
  });
}

/** Friendly message for an error code (technical details never reach the user). */
export function useErrorMessage() {
  const { t, i18n } = useTranslation();
  return (err: unknown, fallback: 'generic' | 'load' = 'generic') => {
    if (err instanceof ApiError && i18n.exists(`common:errors.${err.code}`)) return t(`errors.${err.code}`);
    return t(`errors.${fallback}`);
  };
}

/** Mutation with toast feedback and targeted invalidation. */
export function useApiMutation<TVars, TData = unknown>(
  fn: (vars: TVars) => Promise<TData>,
  opts: { invalidate?: QueryKey[]; success?: string | ((data: TData) => string | null); onSuccess?: (data: TData, vars: TVars) => void; onError?: (err: unknown) => boolean | void } = {},
) {
  const qc = useQueryClient();
  const message = useErrorMessage();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (data, vars) => {
      await Promise.all((opts.invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey })));
      const msg = typeof opts.success === 'function' ? opts.success(data) : opts.success;
      if (msg) toast.success(msg);
      opts.onSuccess?.(data, vars);
    },
    onError: (err) => {
      if (opts.onError?.(err) === true) return;
      toast.error(message(err));
    },
  });
}
